import type { TFunction } from 'i18next';
import { effectiveMode } from './binding-mode';
import { hasLiveSwitch } from './integration-toggle';
import { canonicalCategory } from './legacy-category';
import type {
  IntegrationDescriptor,
  IntegrationHealth,
  IntegrationHealthState,
  IntegrationProbeResult,
  ServiceCategory,
} from './types';

/** Re-exported for the screens that import it from here; the alias itself lives in `legacy-category.ts`. */
export { canonicalCategory };

/**
 * WHAT THE OPERATOR READS PER INTEGRATION (#840): AVAILABILITY.
 *
 * The Proveedores screen is a health and availability surface. Every wired row
 * carries a `health` the operator-bff computed on the read (probe cache, live
 * traffic, breaker), so no integration sits in a "configured, never tested"
 * limbo any more. Two facts still never collapse:
 *
 *   · DISABLED / NOT WIRED — a descriptor EXISTS and says `enabled:false`. We
 *                       asked, the app answered, it holds nothing (or it was
 *                       switched off).
 *   · UNKNOWN         — NO descriptor at all; the declaring app is in
 *                       `unreachable[]`. Structurally a different record,
 *                       rendered by a different component, so no `!enabled`
 *                       test can ever reach it.
 */

/** What a probe result MEANS. `undefined` in → `'not-tested'`, because never
 *  probed and probed-and-failed are different answers. Mirrors the contract's
 *  `probeVerdict()` so no consumer re-derives a red badge from a bare `ok:false`. */
export type ProbeVerdict = 'pass' | 'fail' | 'not-probeable' | 'not-tested';

export function probeVerdict(result: IntegrationProbeResult | undefined | null): ProbeVerdict {
  if (!result) return 'not-tested';
  if (result.notProbeable) return 'not-probeable';
  return result.ok ? 'pass' : 'fail';
}

/** What a finished probe means for availability — the BFF's `healthFromProbe`. */
export function healthFromProbe(result: IntegrationProbeResult): IntegrationHealth {
  if (result.notProbeable) return { state: 'unverifiable', source: 'declared' };
  if (result.ok) return { state: 'online', source: 'probe', checkedAt: result.testedAt };
  const failed = result.steps.find((s) => !s.ok);
  return {
    state: 'offline',
    source: 'probe',
    checkedAt: result.testedAt,
    ...(failed ? { detail: failed.detail ? `${failed.step}: ${failed.detail}` : failed.step } : {}),
  };
}

/**
 * What configuration alone can say, for a BFF that sent no `health`. Never
 * "checking": nothing is running, and a spinner that never resolves is worse
 * than an honest "not directly verifiable".
 */
export function fallbackHealth(descriptor: IntegrationDescriptor): IntegrationHealth {
  if (!descriptor.enabled) return { state: 'disabled', source: 'declared' };
  if (effectiveMode(descriptor) === 'mock') return { state: 'simulated', source: 'declared' };
  if (!descriptor.credentialsPresent && descriptor.authStrategy !== 'none') {
    return { state: 'no_credentials', source: 'declared' };
  }
  return { state: 'unverifiable', source: 'declared' };
}

/** States a manual probe may override: the ones a probe actually measures. */
const PROBE_MEASURABLE: ReadonlySet<IntegrationHealthState> = new Set([
  'online',
  'offline',
  'checking',
  'unverifiable',
]);

/**
 * The health ONE row renders: the server's, unless the operator just ran
 * "Probar conexión" and that result is newer — then the button's answer shows
 * immediately, without waiting for the re-read. A manual probe never turns a
 * simulated, unconfigured or switched-off row into "online"/"offline".
 */
export function rowHealth(
  descriptor: IntegrationDescriptor,
  manual: IntegrationProbeResult | undefined,
): IntegrationHealth {
  const server = descriptor.health ?? fallbackHealth(descriptor);
  if (!manual || !PROBE_MEASURABLE.has(server.state)) return server;
  const manualAt = Date.parse(manual.testedAt);
  const serverAt = server.checkedAt ? Date.parse(server.checkedAt) : Number.NEGATIVE_INFINITY;
  if (Number.isFinite(manualAt) && manualAt < serverAt) return server;
  const fromManual = healthFromProbe(manual);
  // A "nothing to probe" answer adds nothing over a server verdict that did measure.
  if (fromManual.state === 'unverifiable' && server.state !== 'checking') return server;
  return fromManual;
}

/** Counts per state over the WIRED rows — the summary at the top. */
export type HealthCounts = Record<IntegrationHealthState, number>;

export function healthCounts(descriptors: IntegrationDescriptor[]): HealthCounts {
  const counts: HealthCounts = {
    online: 0,
    offline: 0,
    checking: 0,
    simulated: 0,
    unverifiable: 0,
    no_credentials: 0,
    disabled: 0,
  };
  for (const d of descriptors) {
    if (!d.enabled) continue;
    counts[(d.health ?? fallbackHealth(d)).state] += 1;
  }
  return counts;
}

/**
 * An `unreachable` declarant name paired with its reason, when the inventory
 * sent one. The list is the frozen field; `unknown[]` is the richer, additive
 * form of the same fact — a consumer that only knows about `unreachable` still
 * cannot mistake an unasked app for a configured one.
 */
export interface UnknownEntry {
  declaredBy: string;
  reason?: 'unreachable' | 'timeout' | 'error';
  detail?: string;
  observedAt?: string;
}

export function unknownEntries(inventory: {
  unreachable: string[];
  unknown?: {
    declaredBy: string;
    reason: 'unreachable' | 'timeout' | 'error';
    detail?: string;
    observedAt: string;
  }[];
}): UnknownEntry[] {
  const byName = new Map((inventory.unknown ?? []).map((u) => [u.declaredBy, u] as const));
  const names = inventory.unreachable.length ? inventory.unreachable : [...byName.keys()];
  return names.map((declaredBy) => {
    const detail = byName.get(declaredBy);
    return {
      declaredBy,
      ...(detail ? { reason: detail.reason, observedAt: detail.observedAt } : {}),
      ...(detail?.detail ? { detail: detail.detail } : {}),
    };
  });
}

/* ══════════════════════════════ THE SELECTION MODEL ══════════════════════════
 *
 * `enabled: true` DOES NOT MEAN ONE THING. It means "the app that declared this
 * built the adapter" — and what that implies for traffic depends entirely on how
 * that app consumes the category. Treating the three cases as one is exactly the
 * bug this restructure fixes: the section used to print the whole CATALOGUE of
 * what Nexa supports and leave the operator to infer which entries are real.
 *
 * The question the section must answer is not "what could this run?" but:
 *
 *     PER CATEGORY — what is wired here, and where does traffic actually go?
 *
 * So each category declares how its entries relate to one another:
 *
 *   · 'single'   — the app builds EXACTLY ONE provider for this category, chosen
 *                  by a single env var (`REBOOKING_INVENTORY_PROVIDER`,
 *                  `TRANSPORT_ADAPTER_DEFAULT`). Everything else in the category
 *                  is an ALTERNATIVE SUPPLIER that carries no traffic. Naming
 *                  them next to the wired one is what made the old grid unreadable,
 *                  so they collapse into one muted "also supported" line.
 *
 *   · 'fanout'   — the app builds a COMPOSITE and may consult several at once
 *                  (booking's hotel search). Every enabled entry is genuinely in
 *                  the path, so every one is named; the rest collapse as above.
 *
 *   · 'channels' — each entry is an INDEPENDENT CAPABILITY, not an alternative
 *                  supplier. "No WhatsApp" does not mean traffic goes elsewhere;
 *                  it means THIS INSTANCE CANNOT SEND WHATSAPP AT ALL. That is a
 *                  fact about the instance's reach and the operator must see it,
 *                  so channels are named individually whether wired or not, and
 *                  are NEVER collapsed into an "also supported" line.
 *
 * A category the wire grows before this file learns about it falls back to
 * 'channels' (see {@link categorySelection}): naming every entry is the lossless
 * choice, and collapsing is the one that can hide a fact.
 */
export type CategorySelection = 'single' | 'fanout' | 'channels' | 'precedence';

/** Keyed by SERVICE: a legacy spelling reads its service's policy (see {@link categorySelection}). */
export const CATEGORY_SELECTION: Record<ServiceCategory, CategorySelection> = {
  /** booking builds a composite hotel provider and may search several vendors. */
  hotel: 'fanout',
  /** rebooking builds ONE inventory provider (`REBOOKING_INVENTORY_PROVIDER`). */
  'air-ticketing': 'single',
  /** A FUSION, per flight and per field: the airline wins, the predictor fills in (D3). */
  'flight-info': 'precedence',
  /** Per flight: uploaded file > airline adapter > synthetic (D11). */
  'passenger-data': 'precedence',
  /** transport builds ONE adapter (`TRANSPORT_ADAPTER_DEFAULT`). */
  transport: 'single',
  /** SMS / WhatsApp / voice / email are separate reaches, not substitutes. */
  notifications: 'channels',
  /** Card issuing and pass generation are separate capabilities, not substitutes. */
  wallet: 'channels',
  /** Each AI feature's app builds the one LLM provider its env names; `ai/generate` orders them (integrations-tds §16). */
  ai: 'single',
  /** Exporters run side by side; each is independently on or off. */
  observability: 'channels',
};

/** Unlisted categories name every entry rather than risk collapsing a fact away. */
export const DEFAULT_CATEGORY_SELECTION: CategorySelection = 'channels';

export function categorySelection(category: string): CategorySelection {
  return CATEGORY_SELECTION[canonicalCategory(category) as ServiceCategory] ?? DEFAULT_CATEGORY_SELECTION;
}

/**
 * The order the operator reads categories in — the order they matter during a
 * disruption. Anything else the wire carries follows, alphabetically, so a new
 * category appears rather than silently disappearing.
 */
export const CATEGORY_ORDER: ServiceCategory[] = [
  'flight-info',
  'passenger-data',
  'air-ticketing',
  'hotel',
  'transport',
  'notifications',
  'wallet',
];

/** Same binding (vendor × service), whichever spelling of the category each side carries. */
export function sameBinding(
  a: { vendor: string; category: string },
  b: { vendor: string; category: string },
): boolean {
  return a.vendor === b.vendor && canonicalCategory(a.category) === canonicalCategory(b.category);
}

/** One category block: what is in the path, and what is merely supported. */
export interface CategoryGroup {
  category: string;
  selection: CategorySelection;
  /** `enabled` — the declaring app BUILT this. Traffic goes here. */
  wired: IntegrationDescriptor[];
  /** Supported by this build, carrying nothing. */
  unwired: IntegrationDescriptor[];
  /** `false` for 'channels': an unwired channel is a missing reach, so it is
   *  named individually instead of being folded into a list of alternatives. */
  collapseUnwired: boolean;
}

/**
 * `vendor` alone is NOT unique — the live inventory carries `amadeus`, `sabre`
 * and `mock` in two categories each. Keys and test ids must be qualified or two
 * unrelated integrations share a React key and a DOM node.
 */
export function integrationKey(descriptor: { category: string; vendor: string }): string {
  return `${canonicalCategory(descriptor.category)}-${descriptor.vendor}`;
}

/**
 * Group the flat inventory into the per-SERVICE blocks the section renders. A
 * row under a legacy spelling joins its service ({@link canonicalCategory});
 * should one vendor arrive under both spellings, the row already carrying the
 * service's own name wins.
 */
export function groupByCategory(integrations: IntegrationDescriptor[]): CategoryGroup[] {
  const byCategory = new Map<string, IntegrationDescriptor[]>();
  for (const descriptor of integrations) {
    const category = canonicalCategory(descriptor.category);
    const list = byCategory.get(category) ?? [];
    // A twin = the same vendor under the OTHER spelling of this service.
    const twin = list.findIndex((d) => d.vendor === descriptor.vendor && d.category !== descriptor.category);
    if (twin < 0) list.push(descriptor);
    else if (descriptor.category === category) list[twin] = descriptor;
    byCategory.set(category, list);
  }

  const known = CATEGORY_ORDER.filter((c) => byCategory.has(c));
  const rest = [...byCategory.keys()]
    .filter((c) => !(CATEGORY_ORDER as string[]).includes(c))
    .sort();

  return [...known, ...rest].map((category) => {
    const entries = byCategory.get(category) ?? [];
    const selection = categorySelection(category);
    return {
      category,
      selection,
      // #839: a row with a live switch stays a row while OFF — folding it into
      // the "also supported" line would hide the switch that turns it back on.
      wired: entries.filter((d) => d.enabled || hasLiveSwitch(d)),
      unwired: entries.filter((d) => !d.enabled && !hasLiveSwitch(d)),
      collapseUnwired: selection !== 'channels',
    };
  });
}

/**
 * THE NAME THE OPERATOR READS. Never the raw wire token.
 *
 * Resolution order, and why each step exists:
 *
 *   1. `vendorName.<category>.<vendor>` — REQUIRED, because a token is not
 *      unique: `mock` is "Nexa Inventory (simulated)" under `hotel` and
 *      "Simulated transport" under `transport`, and a single flat map would
 *      have to lie about one of them.
 *   2. `vendorName.<vendor>` — the usual case: one vendor, one brand name,
 *      spelled the way the vendor spells it (`addpass.io`, not `Addpass.io` —
 *      which is why the title carries no CSS `capitalize`).
 *   3. Prettified token — `airline-schedule` → `Airline schedule`. A last
 *      resort for a vendor the map has not learned yet, so a new wire token
 *      still reads as a name instead of as a slug.
 *
 * A token containing a `.` would be read by i18next as a nested path, so it
 * skips the map rather than resolving to the wrong node.
 */
export function vendorLabel(t: TFunction<'network'>, category: string, vendor: string): string {
  if (!vendor.includes('.')) {
    // A legacy spelling reads its service's names.
    const scoped = t(`providers.integrations.vendorName.${canonicalCategory(category)}.${vendor}`, {
      defaultValue: '',
    });
    if (scoped) return scoped;
    const plain = t(`providers.integrations.vendorName.${vendor}`, { defaultValue: '' });
    if (plain) return plain;
  }
  return prettifyVendor(vendor);
}

/** `airline-schedule` → `Airline schedule`. Never the bare token. */
function prettifyVendor(vendor: string): string {
  const spaced = vendor.replace(/[-_]+/g, ' ').trim();
  if (!spaced) return vendor;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
